import { Dispatch } from '@reduxjs/toolkit';
import { requestHandler } from 'lib/api';
import { PortfolioStatus, PortfolioTemplate } from 'lib/types/models';

import {
  CreateDocuments,
  CreatePortfolioFromTemplate,
  SignDocumentsResponse,
} from './types';

export const getOriginalDocumentFile = async (
  dispatch: Dispatch,
  documentId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/documentFile/original/${documentId}`,
    dispatch,
    responseType: 'blob',
  });
  if (response) {
    return {
      blob: response.data as Blob,
      contentType: response.headers['content-type'] ?? 'application/pdf',
    };
  }
};

export const getSignedDocumentFile = async (
  dispatch: Dispatch,
  documentId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/documentFile/signed/${documentId}`,
    dispatch,
    responseType: 'blob',
  });
  if (response) {
    return {
      blob: response.data as Blob,
      contentType: response.headers['content-type'] ?? 'application/pdf',
    };
  }
};

export const getPortfolios = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/portfolios/${businessIdentifier}?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getPortfoliosTemplates = async (
  dispatch: Dispatch,
  businessIdentifier?: string,
): Promise<PortfolioTemplate[]> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/templates?${businessIdentifier ? `businessIdentifier=${businessIdentifier}` : ''}`,
    dispatch,
  });
  if (response) return response.data;
  return [];
};

export const getPortoflioValidations = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/validations`,
    dispatch,
  });
  if (response) return response.data;
};

export const getTemplateTriggers = async (dispatch: Dispatch) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/triggers`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSignerInformation = async (dispatch: Dispatch, nin: string) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/signer/${nin}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSignDocuments = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  query: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/digitalSignature/signDocuments/${businessIdentifier}/?${query}`,
    dispatch,
  });
  if (response) return response.data as SignDocumentsResponse;
};

export const createDocuments = async (
  dispatch: Dispatch,
  portfolioId: string,
  data: CreateDocuments,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/digitalSignature/${portfolioId}/documents`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const createPortfolio = async (dispatch: Dispatch, data: FormData) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/digitalSignature/portfolios`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const startSignProcess = async (
  dispatch: Dispatch,
  portfolioId: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/digitalSignature/${portfolioId}/startSignProcess`,
    dispatch,
  });
  if (response) return response.data;
};

export const createPortfolioFromTemplate = async (
  dispatch: Dispatch,
  data: CreatePortfolioFromTemplate,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/digitalSignature/portfolios/template`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const updatePortfolio = async (
  dispatch: Dispatch,
  portfolioId: string,
  data: { status: PortfolioStatus },
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/digitalSignature/portfolios/${portfolioId}`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const updatePortfolioValidation = async (
  dispatch: Dispatch,
  portfolioId: string,
  data: { validations: string[] },
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/digitalSignature/portfolios/${portfolioId}/validations`,
    dispatch,
    data,
  });
  if (response) return response.data;
};

export const updateTemplate = async (
  dispatch: Dispatch,
  templateId: string,
  data: { signers: number[] },
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/digitalSignature/templates/${templateId}`,
    dispatch,
    data,
  });
  if (response) return response.data;
};
